// ts-rest contract for the Vulnerability Assessment (SEP-171).
//
// A `VA-####` record per assessment occasion, stacked on a Contact over time.
// The Contact detail page needs three things from this contract:
//   - the summary the Vulnerability card shows (latest COMPLETED band, its
//     completion date and its next review date),
//   - the stacked history rows (Record Name | Completed By | Score | Band |
//     Completed Date | Next Review Date),
//   - the full assessment record the questionnaire form edits.
//
// Only the selected option values, the captured `clientAgeYears` and the two
// notes boxes are stored. The per-question score, the total, the band, the
// review frequency and the next review date are all computed by the shared
// `computeVulnerabilityAssessment` helper (@septima/contract) — the web form
// recomputes the identical figures live, so the `derived` block is a mirror,
// not the source of truth. The question table itself is imported directly from
// the package (`VULNERABILITY_QUESTIONS`); it is not an endpoint.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';

const c = initContract();

import { errorBody } from '../schemas/common';

/**
 * `superseded` (SEP-179) — a record a later correction restates. It stays in
 * the history grid as evidence; every "current assessment" read filters on
 * `completed`.
 */
const statusSchema = z.enum(['draft', 'completed', 'superseded']);
const bandSchema = z.enum(['green', 'yellow', 'red']);

/**
 * Which questionnaire a stored answer map was taken against (mirror of
 * `VulnerabilityAnswerInstrument`). Exported because every surface that shows a
 * score or a band has to know whether that figure is scoreable at all — a row
 * answered against the retired pre-SEP-171 instrument scores 0, and a 0 must
 * never be presented as a genuine Green.
 */
export const vulnerabilityAnswersInstrumentSchema = z.enum(['empty', 'current', 'legacy']);
const answersInstrumentSchema = vulnerabilityAnswersInstrumentSchema;

/**
 * The stored answers: question key → the selected option `value`. Only answered
 * questions are present. Values are the closed snake_case option strings
 * declared in `VULNERABILITY_QUESTIONS`.
 */
export const vulnerabilityAnswersSchema = z.record(z.string(), z.string());
export type VulnerabilityAnswersDto = z.infer<typeof vulnerabilityAnswersSchema>;

/** Controller-derived figures (mirror of `computeVulnerabilityAssessment`). */
export const vulnerabilityAssessmentDerivedSchema = z.object({
    /** Sum of the selected options' scores, including the Age Factor. */
    score: z.number(),
    /** Count of answerable questions with a recognised answer. */
    answered: z.number(),
    /** Number of answerable questions (excludes the automated Age Factor). */
    answerable: z.number(),
    /** Per-question contribution, keyed by question key — the form's score boxes. */
    questionScores: z.record(z.string(), z.number()),
    /** The Age Factor option the captured age resolved to (e.g. `age_65_74`). */
    clientAgeOption: z.string().nullable(),
    band: bandSchema,
    /** "GREEN BAND - Not Vulnerable" etc. */
    bandLabel: z.string(),
    /** "Green" / "Yellow" / "Red" — the card + grid label. */
    bandShortLabel: z.string(),
    /** 18 / 9 / 6 — the Required Review Frequency. */
    reviewFrequencyMonths: z.number(),
    /**
     * Whether the stored answers key into the CURRENT question set. `legacy`
     * means the row was taken against the retired pre-SEP-171 15-question
     * instrument: it scores 0 because nothing it holds is scoreable, and a
     * surface should label it rather than present that 0 as a real Green.
     */
    answersInstrument: answersInstrumentSchema,
});

export const vulnerabilityAssessmentDtoSchema = z.object({
    id: z.string(),
    /** VA-#### — null on rows created before SEP-171. */
    reference: z.string().nullable(),
    /** Legacy flat-Client key — null for Contact-keyed assessments (SEP-168). */
    clientId: z.string().nullable(),
    /** Three-tier Contact key — null for legacy flat-Client rows (SEP-168). */
    contactId: z.string().nullable(),
    /** The owning IM (a `users` row id). */
    imUserId: z.string(),
    status: statusSchema,
    answers: vulnerabilityAnswersSchema,
    /** Age in whole years captured from the Contact's DOB at save time. */
    clientAgeYears: z.number().nullable(),
    rationalityNotes: z.string().nullable(),
    supportPlanNotes: z.string().nullable(),
    derived: vulnerabilityAssessmentDerivedSchema,
    /** Calendar completion date (`YYYY-MM-DD`) — null while draft. */
    completionDate: z.string().nullable(),
    /** completionDate + the band's review frequency — null while draft. */
    nextReviewDate: z.string().nullable(),
    createdAt: z.string(),
    /** Completion timestamp — null while draft. */
    completedAt: z.string().nullable(),
    /** The "Completed By" staff user id — null while draft. */
    completedByUserId: z.string().nullable(),
    /** Display name for `completedByUserId`, resolved server-side. */
    completedByName: z.string().nullable(),
    // --- Amendment chain (SEP-179) ---
    /** On a CORRECTION: the id of the assessment it restates. */
    supersedesId: z.string().nullable(),
    /** On a CORRECTION: that assessment's VA-####, resolved server-side. */
    supersedesReference: z.string().nullable(),
    /** On a SUPERSEDED record: the id of the correction that replaced it. */
    supersededById: z.string().nullable(),
    /** On a SUPERSEDED record: that correction's VA-####. */
    supersededByReference: z.string().nullable(),
    /** When the correction was recorded. */
    supersededAt: z.string().nullable(),
    /** Who recorded the correction. */
    supersededByUserId: z.string().nullable(),
    /** Display name for `supersededByUserId`, resolved server-side. */
    supersededByName: z.string().nullable(),
    /** On a CORRECTION: the free-text reason given. */
    amendmentReason: z.string().nullable(),
});
export type VulnerabilityAssessmentDto = z.infer<typeof vulnerabilityAssessmentDtoSchema>;

/**
 * What the Contact's Vulnerability card shows: the band of the most recently
 * COMPLETED assessment plus its dates. Every field is null when the Contact has
 * no completed assessment.
 *
 * `answersInstrument` is load-bearing, not decorative: when the most recent
 * completed assessment was NOT taken against the current question set, `band`,
 * `bandShortLabel`, `score` and `nextReviewDate` are all null and the card must
 * read "Not scored" rather than inventing a reassuring Green from a score of 0.
 * The identifying fields (`assessmentId`, `reference`, `completionDate`) still
 * carry, so the card can say WHICH record it is declining to score.
 */
export const contactVulnerabilitySummarySchema = z.object({
    /** Assessment id the summary was taken from. */
    assessmentId: z.string().nullable(),
    reference: z.string().nullable(),
    band: bandSchema.nullable(),
    /** "Green" / "Yellow" / "Red". */
    bandShortLabel: z.string().nullable(),
    score: z.number().nullable(),
    /** "Most recent review date" on the card. */
    completionDate: z.string().nullable(),
    /** "Next assessment date" on the card. */
    nextReviewDate: z.string().nullable(),
    /**
     * The instrument the summarised assessment was answered against; null when
     * there is no completed assessment at all. Anything other than `current`
     * means the score/band fields above are deliberately null.
     */
    answersInstrument: answersInstrumentSchema.nullable(),
});
export type ContactVulnerabilitySummary = z.infer<typeof contactVulnerabilitySummarySchema>;

/**
 * Contact-keyed surface: the Vulnerability card summary + every assessment held
 * against the person, NEWEST FIRST. (The mock renders the history oldest-first;
 * that is a presentation choice for the UI, not the transport order.)
 */
export const listContactVulnerabilityAssessmentsResponseSchema = z.object({
    contactId: z.string(),
    contactName: z.string(),
    /** The Contact's date of birth, for the form's automated Age Factor. */
    dateOfBirth: z.string().nullable(),
    /** Age in whole years as at now — what a NEW assessment would capture. */
    currentAgeYears: z.number().nullable(),
    summary: contactVulnerabilitySummarySchema,
    assessments: z.array(vulnerabilityAssessmentDtoSchema),
});
export type ListContactVulnerabilityAssessmentsResponse = z.infer<
    typeof listContactVulnerabilityAssessmentsResponseSchema
>;

// GET response for the legacy flat-Client surface: the current assessment
// (`exists: true`) or null. `clientName` is always present for the page header.
export const vulnerabilityAssessmentGetResponseSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    exists: z.boolean(),
    /**
     * The Client's age in whole years as at now — what a NEW assessment would
     * capture. The page needs it to preview the automated Age Factor: without
     * it the live total silently omits up to 15 points before the first save,
     * and the header jumps band the moment the draft is stored.
     */
    currentAgeYears: z.number().nullable(),
    assessment: vulnerabilityAssessmentDtoSchema.nullable(),
});
export type VulnerabilityAssessmentGetResponse = z.infer<
    typeof vulnerabilityAssessmentGetResponseSchema
>;

/** Save body — the questionnaire's editable fields. Nothing derived is sent. */
const saveBodySchema = z.object({
    /** Absent → create a new draft; present → patch that draft. */
    id: z.string().optional(),
    answers: vulnerabilityAnswersSchema,
    rationalityNotes: z.string().nullable().optional(),
    supportPlanNotes: z.string().nullable().optional(),
});

/**
 * Amend body (SEP-179) — the corrected content of a COMPLETED assessment.
 *
 * There is no `id` in the body: the record being corrected is the path
 * parameter, and the correction's own VA-#### is allocated server-side. The
 * assessment occasion (completion date, completed-by, and the captured
 * `clientAgeYears`) is inherited from the original and is deliberately not
 * editable here — this endpoint fixes what was recorded, not when it happened.
 */
const amendBodySchema = z.object({
    answers: vulnerabilityAnswersSchema,
    rationalityNotes: z.string().nullable().optional(),
    supportPlanNotes: z.string().nullable().optional(),
    /** Why the record is being corrected. Optional free text. */
    amendmentReason: z.string().nullable().optional(),
});

/**
 * Firm-wide, de-identified vulnerability oversight aggregate — the leadership
 * replacement for row-level access (the `view` persona holds no read on the
 * `VulnerabilityAssessment` Object Type). Counts only: no row ids, no client
 * identifiers, no free text. Every count is over SUBJECTS (a Contact, or a
 * legacy flat-Client), classified by their most recently COMPLETED assessment
 * — the same "current assessment" rule every other surface applies.
 */
export const vulnerabilityBandCountsSchema = z.object({
    green: z.number().int(),
    yellow: z.number().int(),
    red: z.number().int(),
});

/**
 * Subjects whose current assessment shows a positive contribution from each
 * instrument category — the "what is driving vulnerability" dimension. Counted
 * over scoreable current assessments only; one subject can appear under
 * several drivers.
 */
export const vulnerabilityDriverCountsSchema = z.object({
    ageFactor: z.number().int(),
    healthAssessment: z.number().int(),
    economicAbuse: z.number().int(),
    lifeEvents: z.number().int(),
    resilience: z.number().int(),
    capability: z.number().int(),
});

export const vulnerabilityOversightSummarySchema = z.object({
    /** When the aggregate was computed (firm clock, ISO). */
    asOf: z.string(),
    /** Subjects holding at least one completed assessment. */
    assessedSubjects: z.number().int(),
    /** Subjects by the band of their current (latest completed) assessment. */
    bandCounts: vulnerabilityBandCountsSchema,
    /**
     * Subjects whose current assessment was answered against the retired
     * pre-SEP-171 instrument and therefore carries no honest score or band.
     */
    notScored: z.number().int(),
    /** Draft assessments currently open across the firm. */
    draftsInProgress: z.number().int(),
    driverCounts: vulnerabilityDriverCountsSchema,
});
export type VulnerabilityOversightSummary = z.infer<typeof vulnerabilityOversightSummarySchema>;

/** Complete body — the "Completed By" decision (defaults to the caller). */
const completeBodySchema = z
    .object({
        /**
         * The IM recorded as having completed the assessment (the CRM's
         * searchable list of IMs). Omitted → the signed-in user.
         */
        completedByUserId: z.string().optional(),
    })
    .optional();

export const vulnerabilityAssessmentContract = c.router(
    {
        getVulnerabilityOversightSummary: {
            method: 'GET',
            path: '/vulnerability/oversight-summary',
            responses: {
                200: vulnerabilityOversightSummarySchema,
                403: errorBody,
            },
            summary:
                'Firm-wide de-identified vulnerability counts (bands, drivers) for leadership oversight',
        },
        listContactVulnerabilityAssessments: {
            method: 'GET',
            path: '/contacts/:contactId/vulnerability-assessments',
            pathParams: z.object({ contactId: z.string() }),
            responses: {
                200: listContactVulnerabilityAssessmentsResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "A Contact's vulnerability card summary + assessment history (newest first)",
        },
        saveContactVulnerabilityAssessment: {
            method: 'POST',
            path: '/contacts/:contactId/vulnerability-assessments/save',
            pathParams: z.object({ contactId: z.string() }),
            body: saveBodySchema,
            responses: {
                200: z.object({ assessment: vulnerabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: "Create or patch a Contact's draft vulnerability assessment",
        },
        getVulnerabilityAssessment: {
            method: 'GET',
            path: '/desk/vulnerability/assessment/:clientId',
            pathParams: z.object({ clientId: z.string() }),
            responses: {
                200: vulnerabilityAssessmentGetResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "A client's Vulnerability Assessment (or a blank)",
        },
        saveVulnerabilityAssessment: {
            method: 'POST',
            path: '/desk/vulnerability/assessment/save',
            body: saveBodySchema.extend({ clientId: z.string().min(1) }),
            responses: {
                200: z.object({ assessment: vulnerabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Create or patch the working draft of a vulnerability assessment',
        },
        completeVulnerabilityAssessment: {
            method: 'POST',
            path: '/desk/vulnerability/assessment/:id/complete',
            pathParams: z.object({ id: z.string() }),
            body: completeBodySchema,
            responses: {
                200: z.object({ assessment: vulnerabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Complete a vulnerability assessment (draft → completed)',
        },
        amendVulnerabilityAssessment: {
            method: 'POST',
            path: '/contacts/vulnerability-assessments/:id/amend',
            pathParams: z.object({ id: z.string() }),
            body: amendBodySchema,
            responses: {
                // The CORRECTION, not the record that was corrected.
                200: z.object({ assessment: vulnerabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Correct a completed vulnerability assessment (supersedes the original)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
